import type {
    SalesFlowExitRules,
    SalesFlowHandle,
    SalesFlowNodeType,
    SalesFlowRunStatus,
    SalesFlowTriggerType,
} from '@mediardex/contracts'
import type { ReactNode } from 'react'
import {
    MdAccountBalanceWallet,
    MdAltRoute,
    MdCallSplit,
    MdChat,
    MdEmail,
    MdFlag,
    MdLabel,
    MdLink,
    MdNoteAdd,
    MdNotificationsActive,
    MdPause,
    MdPersonAdd,
    MdPlayArrow,
    MdPlaylistAdd,
    MdStop,
    MdSwapHoriz,
    MdTaskAlt,
    MdThermostat,
    MdTimeline,
    MdViewKanban,
    MdWebhook,
    MdEditNote,
    MdDescription,
} from 'react-icons/md'

export type FlowNodeGroup = 'start' | 'message' | 'wait' | 'crm' | 'system'

export interface FlowNodeMeta {
    label: string
    description: string
    group: FlowNodeGroup
    icon: ReactNode
    /** Oculto na paleta (ex.: tarefa, que só entra na fase 6). */
    hidden?: boolean
}

export const FLOW_NODE_META: Record<SalesFlowNodeType, FlowNodeMeta> = {
    START: {
        label: 'Início',
        description: 'Onde o fluxo começa',
        group: 'start',
        icon: <MdPlayArrow />,
    },
    SEND_WHATSAPP: {
        label: 'Enviar WhatsApp',
        description: 'Mensagem de texto para o lead ou para a equipe',
        group: 'message',
        icon: <MdChat />,
    },
    SEND_TEMPLATE: {
        label: 'Enviar template',
        description: 'Template aprovado da Meta',
        group: 'message',
        icon: <MdDescription />,
    },
    SEND_EMAIL: {
        label: 'Enviar e-mail',
        description: 'E-mail para o lead ou para a equipe',
        group: 'message',
        icon: <MdEmail />,
    },
    NOTIFY_USER: {
        label: 'Notificar usuário',
        description: 'Aviso interno para a equipe',
        group: 'message',
        icon: <MdNotificationsActive />,
    },
    PAUSE: {
        label: 'Aguardar',
        description: 'Espera um tempo ou a resposta do contato',
        group: 'wait',
        icon: <MdPause />,
    },
    CONDITION: {
        label: 'Condição',
        description: 'Divide o caminho em sim e não',
        group: 'wait',
        icon: <MdCallSplit />,
    },
    GO_TO: {
        label: 'Ir para',
        description: 'Pula para outro passo',
        group: 'wait',
        icon: <MdAltRoute />,
    },
    MOVE_STAGE: {
        label: 'Mover etapa',
        description: 'Leva a oportunidade para uma etapa',
        group: 'crm',
        icon: <MdViewKanban />,
    },
    ASSIGN_USER: {
        label: 'Atribuir responsável',
        description: 'Define o responsável pelo lead',
        group: 'crm',
        icon: <MdPersonAdd />,
    },
    ASSIGN_WALLET: {
        label: 'Mover para carteira',
        description: 'Carteira do cliente vinculado',
        group: 'crm',
        icon: <MdAccountBalanceWallet />,
    },
    TAG: {
        label: 'Tag',
        description: 'Adiciona ou remove uma tag do lead',
        group: 'crm',
        icon: <MdLabel />,
    },
    SET_FIELD: {
        label: 'Alterar campo',
        description: 'Campo do lead ou da oportunidade',
        group: 'crm',
        icon: <MdEditNote />,
    },
    SET_TEMPERATURE: {
        label: 'Temperatura',
        description: 'Frio, morno ou quente',
        group: 'crm',
        icon: <MdThermostat />,
    },
    SET_LIFECYCLE: {
        label: 'Ciclo de vida',
        description: 'Etapa do ciclo de vida do lead',
        group: 'crm',
        icon: <MdTimeline />,
    },
    CREATE_DEAL: {
        label: 'Criar oportunidade',
        description: 'Nova oportunidade num funil',
        group: 'crm',
        icon: <MdPlaylistAdd />,
    },
    ADD_NOTE: {
        label: 'Registrar nota',
        description: 'Nota na linha do tempo do lead',
        group: 'crm',
        icon: <MdNoteAdd />,
    },
    CREATE_TASK: {
        label: 'Criar tarefa',
        description: 'Tarefa na agenda do responsável',
        group: 'crm',
        icon: <MdTaskAlt />,
    },
    WEBHOOK: {
        label: 'Webhook',
        description: 'Chama um sistema externo',
        group: 'system',
        icon: <MdWebhook />,
    },
    START_FLOW: {
        label: 'Iniciar outro fluxo',
        description: 'Dispara outro fluxo para o lead',
        group: 'system',
        icon: <MdSwapHoriz />,
    },
    STOP: {
        label: 'Encerrar',
        description: 'Termina o fluxo aqui',
        group: 'system',
        icon: <MdStop />,
    },
}

export const FLOW_NODE_GROUPS: { id: FlowNodeGroup; label: string }[] = [
    { id: 'message', label: 'Mensagens' },
    { id: 'wait', label: 'Esperar e decidir' },
    { id: 'crm', label: 'CRM' },
    { id: 'system', label: 'Sistema' },
]

/** Classes de cor por grupo (cabeçalho do cartão e alças). */
export const FLOW_GROUP_STYLE: Record<
    FlowNodeGroup,
    { header: string; ring: string; handle: string }
> = {
    start: {
        header: 'bg-success text-success-content',
        ring: 'border-success/60',
        handle: '!bg-success',
    },
    message: {
        header: 'bg-primary text-primary-content',
        ring: 'border-primary/50',
        handle: '!bg-primary',
    },
    wait: {
        header: 'bg-warning text-warning-content',
        ring: 'border-warning/60',
        handle: '!bg-warning',
    },
    crm: {
        header: 'bg-secondary text-secondary-content',
        ring: 'border-secondary/50',
        handle: '!bg-secondary',
    },
    system: {
        header: 'bg-neutral text-neutral-content',
        ring: 'border-neutral/40',
        handle: '!bg-neutral',
    },
}

export const FLOW_HANDLE_LABELS: Record<SalesFlowHandle, string> = {
    next: 'Depois',
    true: 'Sim',
    false: 'Não',
    replied: 'Respondeu',
    timeout: 'Tempo esgotado',
    failed: 'Se falhar',
}

export const FLOW_TRIGGER_LABELS: Record<SalesFlowTriggerType, string> = {
    EVENT: 'Evento do CRM',
    STAGE_ENTERED: 'Entrou na etapa',
    TIME_IN_STAGE: 'Parado na etapa',
    DATE_FIELD_OFFSET: 'Antes ou depois de uma data',
    NO_REPLY: 'Sem resposta do contato',
    INBOUND_AFTER_SILENCE: 'Mensagem após silêncio',
    OUTBOUND_READ: 'Mensagem lida',
    CONVERSATION_CLOSED: 'Conversa encerrada',
    SCHEDULE_DAILY: 'Todo dia em um horário',
    MANUAL: 'Manual (pela barra do chat)',
}

export const FLOW_EXIT_RULE_LABELS: Record<
    keyof SalesFlowExitRules,
    { label: string; hint: string }
> = {
    onReply: {
        label: 'O contato respondeu',
        hint: 'Pausas "aguardando resposta" seguem pela saída Respondeu em vez de parar.',
    },
    onStageChange: {
        label: 'A oportunidade mudou de etapa',
        hint: 'Mudança feita pelo próprio fluxo não conta.',
    },
    onDealClosed: { label: 'A oportunidade foi ganha ou perdida', hint: '' },
    onBillingPaid: { label: 'A cobrança foi paga', hint: '' },
    onAppointmentCreated: { label: 'Um atendimento foi agendado', hint: '' },
}

export const FLOW_RUN_STATUS: Record<
    SalesFlowRunStatus,
    { label: string; badge: string }
> = {
    RUNNING: { label: 'Executando', badge: 'badge-info' },
    WAITING: { label: 'Aguardando', badge: 'badge-warning' },
    COMPLETED: { label: 'Concluída', badge: 'badge-success' },
    STOPPED: { label: 'Parada', badge: 'badge-neutral' },
    CANCELLED: { label: 'Cancelada', badge: 'badge-ghost' },
    FAILED: { label: 'Falhou', badge: 'badge-error' },
}

export const FLOW_LOG_STATUS: Record<string, { label: string; badge: string }> =
    {
        EXECUTED: { label: 'Executado', badge: 'badge-success' },
        SIMULATED: { label: 'Simulado', badge: 'badge-info' },
        WAITING: { label: 'Aguardando', badge: 'badge-warning' },
        SKIPPED: { label: 'Não executado', badge: 'badge-neutral' },
        FAILED: { label: 'Falhou', badge: 'badge-error' },
    }

/** Minutos em texto legível: 1440 -> "1 dia", 90 -> "1 h 30 min". */
export function formatMinutes(minutes: number | null | undefined): string {
    const total = Math.max(0, Math.round(Number(minutes) || 0))
    if (total === 0) return 'imediatamente'
    const days = Math.floor(total / 1440)
    const hours = Math.floor((total % 1440) / 60)
    const mins = total % 60
    const parts: string[] = []
    if (days) parts.push(`${days} ${days === 1 ? 'dia' : 'dias'}`)
    if (hours) parts.push(`${hours} h`)
    if (mins) parts.push(`${mins} min`)
    return parts.join(' ')
}

export const FLOW_ICON_LINK = <MdLink />
export const FLOW_ICON_FLAG = <MdFlag />
